import type { CellaNumeroProps } from "@/components/panoramica/CellaNumero";
import { PopoverMotivi } from "@/components/panoramica/PopoverMotivi";
import type { TipoCanale } from "@/lib/api";
import type { FiltroProdotti } from "@/lib/filtriOfferta";
import {
  df,
  ef,
  giorniDa,
  hrefDaApprovare,
  hrefProdotti,
  hrefStorico,
  nf,
  pf,
  type NumeriRiga,
  type Vista,
} from "@/lib/panoramica";

export interface ContestoCella {
  // null = riga dei totali: niente link, Prodotti lavora un canale alla volta.
  canaleId: string | null;
  etichetta: string;
  adesso: Date;
  // come Approvals.tsx: su Amazon non si approva
  approvabile: boolean;
}

export interface Colonna {
  id: string;
  etichetta: string;
  glossario: string;
  larghezza: number;
  cella: (n: NumeriRiga, ctx: ContestoCella) => CellaNumeroProps;
}

export const VISTE: ReadonlyArray<{ id: Vista; etichetta: string }> = [
  { id: "stato", etichetta: "Stato e margine" },
  { id: "dati", etichetta: "Dati mancanti" },
  { id: "automazione", etichetta: "Automazione" },
];

// Larghezze in px a 1440 (tabella da 1320): nella pagina diventano percentuali.
export const LARGHEZZA_CANALE: Record<TipoCanale, Record<Vista, number>> = {
  buybox: { stato: 243, dati: 300, automazione: 340 },
  price_setter: { stato: 300, dati: 340, automazione: 340 },
};

const prodotti = (ctx: ContestoCella, filtro?: FiltroProdotti) =>
  ctx.canaleId ? hrefProdotti(ctx.canaleId, filtro) : undefined;

const conteggio = (
  id: string,
  etichetta: string,
  glossario: string,
  larghezza: number,
  campo: (n: NumeriRiga) => number,
  filtro: FiltroProdotti,
  tono: CellaNumeroProps["tono"] = "forte",
): Colonna => ({
  id,
  etichetta,
  glossario,
  larghezza,
  cella: (n, ctx) => ({ valore: nf(campo(n)), numero: campo(n), href: prodotti(ctx, filtro), tono }),
});

const offerte = (larghezza: number): Colonna => ({
  id: "offerte",
  etichetta: "Offerte",
  glossario: "Offerte nostre sul canale. Un prodotto venduto su 3 canali conta 3.",
  larghezza,
  cella: (n, ctx) => ({ valore: nf(n.offerte), numero: n.offerte, href: prodotti(ctx), tono: "normale" }),
});

const sottoMargine = (larghezza: number): Colonna => ({
  id: "sotto_margine",
  etichetta: "Sotto margine",
  glossario: "Prezzo di vendita sotto il floor, esclusi i prezzi bloccati a mano. Sotto: mediana in % e caso peggiore in €.",
  larghezza,
  cella: (n, ctx) => ({
    valore: nf(n.sotto_margine),
    numero: n.sotto_margine,
    href: prodotti(ctx, "sotto_margine"),
    tono: "perdita",
    avviso: n.sotto_margine > 0,
    variazione: df(n.delta_sotto_margine),
    sotto:
      n.sotto_margine === 0
        ? null
        : ctx.canaleId
          ? `mediana ${pf(n.sotto_margine_pct ?? 0)} · ${ef(n.sotto_margine_eur ?? 0)}`
          : `peggiore ${ef(n.sotto_margine_eur ?? 0)}`,
  }),
});

const automazione: Colonna[] = [
  {
    id: "scritti",
    etichetta: "Scritti oggi",
    glossario: "Prezzi scritti oggi sul canale. Automatici: prodotti col prezzo cambiato davvero, i già allineati non contano. Manuali: ogni push dal clic. Lo Storico elenca anche i già allineati.",
    larghezza: 280,
    cella: (n, ctx) => ({
      valore: nf(n.scritti_auto + n.scritti_manuali),
      numero: n.scritti_auto + n.scritti_manuali,
      href: ctx.canaleId ? hrefStorico(ctx.canaleId, "scritti") : undefined,
      sotto: `${nf(n.scritti_auto)} auto · ${nf(n.scritti_manuali)} manuali`,
    }),
  },
  {
    id: "fallite",
    etichetta: "Fallite",
    glossario: "Scritture rifiutate dal canale o interrotte, oggi. Porta allo Storico filtrato.",
    larghezza: 230,
    cella: (n, ctx) => ({
      valore: nf(n.fallite),
      numero: n.fallite,
      href: ctx.canaleId ? hrefStorico(ctx.canaleId, "fallite") : undefined,
      tono: "perdita",
      avviso: n.fallite > 0,
    }),
  },
  {
    id: "in_attesa",
    etichetta: "In attesa",
    glossario: "Proposte di prezzo in attesa di approvazione. Sotto: da quanto aspetta la più vecchia.",
    larghezza: 270,
    cella: (n, ctx) => ({
      valore: nf(n.in_attesa),
      numero: n.in_attesa,
      href: ctx.canaleId && ctx.approvabile ? hrefDaApprovare(ctx.canaleId) : undefined,
      sotto:
        n.in_attesa > 0 && n.in_attesa_dal ? `la più vecchia: ${giorniDa(n.in_attesa_dal, ctx.adesso)} g` : null,
    }),
  },
];

const BUYBOX: Record<Vista, Colonna[]> = {
  stato: [
    offerte(110),
    {
      id: "spente",
      etichetta: "Spente",
      glossario: "Offerte che nessuno può comprare (senza giacenza o disattivate). Sotto: quante riaprirebbero sotto margine.",
      larghezza: 167,
      cella: (n, ctx) =>
        n.spente === null
          ? {
              valore: "—",
              numero: null,
              tono: "normale",
              suggerimento: { titolo: "Spente", testo: "AM non legge lo stato del negozio su questo canale." },
            }
          : {
              valore: nf(n.spente),
              numero: n.spente,
              href: prodotti(ctx, "spente"),
              sotto: n.riaprono_sotto_margine > 0 ? `${nf(n.riaprono_sotto_margine)} riaprono sotto margine` : null,
            },
    },
    {
      id: "vinte",
      etichetta: "Vinte",
      glossario: "La buybox è nostra, oppure nessun concorrente vende il prodotto. Sotto: % vinte e quante sono attaccate al floor (entro il 2%).",
      larghezza: 170,
      cella: (n, ctx) => ({
        valore: nf(n.vinte),
        numero: n.vinte,
        href: prodotti(ctx, "vinte"),
        tono: "normale",
        sotto: `${n.vinte_pct === null ? "—" : pf(n.vinte_pct)} · ${nf(n.attaccate_al_floor)} al floor`,
        hrefSotto: prodotti(ctx, "attaccate_al_floor"),
        iconaFloor: n.attaccate_al_floor > 0,
      }),
    },
    {
      id: "recuperabili",
      etichetta: "Recuperabili",
      glossario: "Perse, ma scendendo a concorrente −1 € restiamo sopra il margine minimo.",
      larghezza: 160,
      cella: (n, ctx) => ({
        valore: nf(n.recuperabili),
        numero: n.recuperabili,
        href: prodotti(ctx, "recuperabili"),
        variazione: df(n.delta_recuperabili),
        sotto: n.recuperabili_auto > 0 ? `${nf(n.recuperabili_auto)} in auto` : null,
        sottoAuto: n.recuperabili_auto > 0,
        suggerimento:
          n.recuperabili > 0
            ? {
                titolo: "Recuperabili bloccate",
                testo: (
                  <PopoverMotivi
                    canale={ctx.etichetta}
                    recuperabili={n.recuperabili}
                    inAuto={n.recuperabili_auto}
                    motivi={n.motivi_blocco}
                  />
                ),
              }
            : null,
      }),
    },
    {
      id: "al_limite",
      etichetta: "Al limite",
      glossario: "Perse, e il concorrente vende sotto il nostro floor: seguirlo vorrebbe dire vendere sotto margine. Sotto: di quanto è sotto.",
      larghezza: 150,
      cella: (n, ctx) => ({
        valore: nf(n.al_limite),
        numero: n.al_limite,
        href: prodotti(ctx, "al_limite"),
        variazione: df(n.delta_al_limite),
        sotto: n.al_limite_pct === null ? null : `${ctx.canaleId ? "mediana" : "peggiore"} ${pf(n.al_limite_pct)}`,
      }),
    },
    {
      id: "perse_altro",
      etichetta: "Perse, altro",
      glossario: "Perse, con dati incompleti per decidere.",
      larghezza: 150,
      cella: (n, ctx) => ({
        valore: nf(n.perse_altro),
        numero: n.perse_altro,
        href: prodotti(ctx, "perse_altro"),
        sotto: n.senza_stato > 0 ? `+${nf(n.senza_stato)} senza stato` : null,
        suggerimento:
          n.senza_stato > 0
            ? { titolo: "Senza stato", testo: "Stato non classificabile: se cresce è un difetto." }
            : null,
      }),
    },
    sottoMargine(170),
  ],
  dati: [
    offerte(140),
    conteggio("opportunita", "Opportunità", "Prodotti che non vendiamo su quel canale ma che, a concorrente −1 €, starebbero sopra margine.", 190, (n) => n.opportunita, "opportunita", "info"),
    conteggio("senza_costo", "Senza costo", "Manca il costo d’acquisto: AM non può calcolare il floor.", 170, (n) => n.senza_costo, "senza_costo"),
    conteggio("senza_spedizione", "Senza sped.", "Manca il costo di spedizione per il paese del canale: AM non può calcolare il floor.", 180, (n) => n.senza_spedizione, "senza_spedizione"),
    conteggio("non_misurati", "Non misurati", "Offerte nostre col prezzo non letto dal canale: AM non abbassa un prezzo che non ha visto.", 175, (n) => n.prezzi_non_misurati, "non_misurati"),
    conteggio("solo_listino", "Solo listino", "Abbiamo un prezzo di listino, ma il canale non mostra una nostra offerta.", 165, (n) => n.solo_listino, "solo_listino"),
  ],
  automazione: [offerte(200), ...automazione],
};

const PRICE_SETTER: Record<Vista, Colonna[]> = {
  stato: [
    offerte(140),
    conteggio("a_margine", "A margine", "Offerte il cui prezzo rispetta il margine obiettivo del canale.", 190, (n) => n.a_margine, "a_margine", "normale"),
    {
      id: "da_correggere",
      etichetta: "Da correggere",
      glossario: "Offerte il cui prezzo si discosta dal margine obiettivo: AM le riscrive al prossimo giro.",
      larghezza: 210,
      cella: (n, ctx) => ({
        valore: nf(n.da_correggere),
        numero: n.da_correggere,
        href: prodotti(ctx, "da_correggere"),
        variazione: df(n.delta_da_correggere),
      }),
    },
    conteggio("senza_floor", "Senza floor", "Manca costo o spedizione: AM non può calcolare il prezzo.", 190, (n) => n.senza_floor, "senza_floor"),
    sottoMargine(290),
  ],
  dati: [
    offerte(200),
    conteggio("senza_costo", "Senza costo", "Manca il costo d’acquisto: AM non può calcolare il floor.", 250, (n) => n.senza_costo, "senza_costo"),
    conteggio("senza_spedizione", "Senza sped.", "Manca il costo di spedizione per il paese del canale.", 260, (n) => n.senza_spedizione, "senza_spedizione"),
    conteggio("non_misurati", "Non misurati", "Prezzo della publication non riletto dal canale.", 270, (n) => n.prezzi_non_misurati, "non_misurati"),
  ],
  automazione: [offerte(200), ...automazione],
};

export function colonne(tipo: TipoCanale, vista: Vista): Colonna[] {
  return tipo === "buybox" ? BUYBOX[vista] : PRICE_SETTER[vista];
}
